import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { InvitationTemplateApiService } from '../services/invitation-template-api.service';
import { InvitationTemplateResponse, StylePreset } from '../models/invitation-template.model';
import { DatetimeService } from '../../../shared/services/datetime.service';

@Component({
  selector: 'app-invitation-template-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PageBreadcrumbComponent,
    ButtonComponent
  ],
  templateUrl: './invitation-template-list.component.html'
})
export class InvitationTemplateListComponent implements OnInit {

  // ── List state ──────────────────────────────────────────────────────────────
  templates: InvitationTemplateResponse[] = [];
  loading = false;
  error = '';
  successMessage = '';

  // ── Pagination ──────────────────────────────────────────────────────────────
  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;

  // ── Delete confirm ───────────────────────────────────────────────────────────
  confirmDeleteId: number | null = null;
  deleteLoading = false;

  // ── Set default loading ──────────────────────────────────────────────────────
  setDefaultLoading: number | null = null;

  constructor(
    private templateApi: InvitationTemplateApiService,
    private router: Router,
    private datetimeService: DatetimeService
  ) {}

  ngOnInit(): void {
    this.loadTemplates();
  }

  // ── Data loading ─────────────────────────────────────────────────────────────

  loadTemplates(): void {
    this.loading = true;
    this.error = '';
    this.templateApi.list(this.currentPage, this.pageSize).subscribe({
      next: (res) => {
        this.templates = res.content;
        this.totalElements = res.totalElements;
        this.totalPages = res.totalPages;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar las plantillas de invitación';
        this.loading = false;
      }
    });
  }

  // ── Navigation ────────────────────────────────────────────────────────────────

  nuevaPlantilla(): void {
    this.router.navigate(['/invitation-templates/crear']);
  }

  editarPlantilla(template: InvitationTemplateResponse): void {
    this.router.navigate(['/invitation-templates/editar', template.id]);
  }

  // ── Set default ───────────────────────────────────────────────────────────────

  establecerPorDefecto(template: InvitationTemplateResponse): void {
    if (template.isDefault) return;
    this.setDefaultLoading = template.id;
    this.error = '';

    this.templateApi.setDefault(template.id).subscribe({
      next: () => {
        this.setDefaultLoading = null;
        this.successMessage = `"${template.name}" establecida como plantilla por defecto`;
        this.loadTemplates();
        setTimeout(() => { this.successMessage = ''; }, 5000);
      },
      error: (err) => {
        this.setDefaultLoading = null;
        this.error = err.error?.message || 'Error al establecer la plantilla por defecto';
      }
    });
  }

  // ── Delete ────────────────────────────────────────────────────────────────────

  askDelete(id: number): void {
    this.confirmDeleteId = id;
  }

  cancelDelete(): void {
    this.confirmDeleteId = null;
  }

  confirmDelete(): void {
    if (this.confirmDeleteId === null) return;

    this.deleteLoading = true;
    this.templateApi.deactivate(this.confirmDeleteId).subscribe({
      next: () => {
        this.confirmDeleteId = null;
        this.deleteLoading = false;
        this.loadTemplates();
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al eliminar la plantilla';
        this.confirmDeleteId = null;
        this.deleteLoading = false;
      }
    });
  }

  // ── Pagination ────────────────────────────────────────────────────────────────

  get currentPageDisplay(): number { return this.currentPage + 1; }
  get hasPreviousPage(): boolean { return this.currentPage > 0; }
  get hasNextPage(): boolean { return this.currentPage < this.totalPages - 1; }

  changePage(page: number): void {
    const idx = page - 1;
    if (idx >= 0 && idx < this.totalPages) {
      this.currentPage = idx;
      this.loadTemplates();
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  stylePresetLabel(preset?: StylePreset): string {
    if (!preset) return '—';
    const map: Record<StylePreset, string> = {
      PROFESSIONAL: 'Profesional',
      CASUAL: 'Casual',
      MINIMAL: 'Minimal',
      BRANDED: 'Con marca'
    };
    return map[preset] ?? preset;
  }

  stylePresetColor(preset?: StylePreset): string {
    if (!preset) return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
    const map: Record<StylePreset, string> = {
      PROFESSIONAL: 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400',
      CASUAL: 'bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400',
      MINIMAL: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
      BRANDED: 'bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400'
    };
    return map[preset] ?? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
  }

  formatDate(iso: string): string {
    if (!iso) return '—';
    return this.datetimeService.formatDateShort(iso);
  }
}
